/**
 * Internal dependencies
 */
import metadata from "./block.json";

import "./../style.scss";
import icons from "./../icons.js";

import {
  timeToString,
  newID,
  PlayerClicked,
  rateMarks,
  masterVolumeMarks,
  fingerMaxAngle,
  translateDialedNumber,
  Key,
} from "../script";

import { AudioDescription, speedIcon } from "../elmercat";

/**
 * WordPress dependencies
 */

import { __, _x } from "@wordpress/i18n";
import {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
} from "@wordpress/element";

import {
  AnglePickerControl,
  Button,
  Popover,
  Dropdown,
  Disabled,
  PanelBody,
  RangeControl,
  FormToggle,
  Flyout,
  CheckboxControl,
  SelectControl,
  Spinner,
  ToggleControl,
  Toolbar,
  ToolbarDropdownMenu,
  ToolbarGroup,
  ToolbarItem,
  ToolbarButton,
} from "@wordpress/components";

export default function Dial({ attributes, mode, isSelected }) {
  const componentProps = {};
  for (const setting in metadata.attributes) {
    const prop = metadata.attributes[setting]["attribute"];
    componentProps[prop] = attributes[setting];
  }

  if (mode === "save") {
    return (
      <div
        id={attributes.id}
        class={attributes.class}
        {...componentProps}
      >
        * ElmerCat Rotary Telephone Dial
      </div>
    );
  }

  const dial = useRef();
  const audioContext = useRef();
  const audioElement = useRef();
  const audioSource = useRef();

  const [contextRunning, setContextRunning] = useState(false);

  const [valueA, setValueA] = useState(0);
  const [valueB, setValueB] = useState(0);
  const [valueC, setValueC] = useState(0);
  const [valueD, setValueD] = useState(0);
  const lampA = useRef();
  const lampB = useRef();
  const lampC = useRef();
  const lampD = useRef();
  const keyA = useRef();
  const keyB = useRef();
  const keyC = useRef();
  const keyD = useRef();

  const [angle, setAngle] = useState(0);
  const [startDialAngle, setStartDialAngle] = useState(0);
  const [dialedAngle, setDialedAngle] = useState(0);
  const [maxAngle, setMaxAngle] = useState(0);

  const dialInterval = useRef();
  const [dialCount, setDialCount] = useState(0);
  const [dialing, setDialing] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const [dialNormal, setDialNormal] = useState(true);
  const [dialedDigit, setDialedDigit] = useState("");
  const [dialedNumber, setDialedNumber] = useState("");

  useEffect(() => {
    //
    audioContext.current = new AudioContext();

    audioContext.current.onstatechange = (e) => {
      setContextRunning(audioContext.current.state === "running");
      console.log("audioContext.current.state:  " + audioContext.current.state);
    };

    console.log("Rotary dial component mounted");
  }, []);

  useEffect(() => {
    if (dialing === false && dialNormal === true && dialedAngle > 0) {
      console.log("dialedAngle " + dialedAngle);
      const [maxA, digit] = fingerMaxAngle(dialedAngle);
      if (digit >= 0) {
        setStartDialAngle(dialedAngle);
        setMaxAngle(maxA);
        setDialedDigit(digit);
        setAngle(0);
        setDialing(true);
      }
    } else {
      var newAngle = dialedAngle - startDialAngle;
      if (newAngle < 0) {
        newAngle += 360;
      }

      if (newAngle > angle && newAngle < maxAngle) {
        if (newAngle - angle < 20) {
          setAngle(newAngle);
        }
      }
    }
  }, [dialedAngle]);

  useEffect(() => {
    if (angle === 0) {
      if (dialInterval.current !== undefined) {
        clearInterval(dialInterval.current);
        dialInterval.current = undefined;
        setDialedNumber(dialedNumber + dialedDigit);
        setPulsing(false);
      }
      setDialNormal(true);
    } else {
      setDialNormal(false);
    }
  }, [angle]);

  useEffect(() => {
    if (dialing === true) {
      console.log("dialing started");
      dial.current.classList.add("dialing");
    } else if (dialing === false) {
      console.log("dialing ended");
      dial.current.classList.remove("dialing");

      if (dialNormal === false && dialInterval.current === undefined) {
        dialInterval.current = setInterval(restoreDial, 10);
        setPulsing(true);
      }
    }
  }, [dialing]);

  function restoreDial() {
    console.log("angle:  " + angle + " dialCount " + dialCount);
    setDialCount((n) => n + 1);

    setAngle((n) => {
      if (n > 3) {
        return n - 3;
      } else if (n > 1) {
        return n - 1;
      } else return 0;
    });
  }

  useEffect(() => {
    if (pulsing === true) {
      console.log("pulsing started");
      dial.current.classList.add("pulsing");
    } else if (dialing === false) {
      console.log("pulsing ended");
      dial.current.classList.remove("pulsing");
    }
  }, [pulsing]);

  return (
    <div
      ref={dial}
      id={attributes.id}
      class={attributes.class}
      {...componentProps}
    >
      <div class='left-panel'>
        <div class='keystrip-a linekeys'>
          <Key
            label='line one'
            labelFirst={true}
          ></Key>
          <Key
            label='line two'
            labelFirst={true}
          ></Key>
          <Key
            label='line three'
            labelFirst={true}
          ></Key>
          <Key
            label='line four'
            labelFirst={true}
          ></Key>
          <Key
            label='line five'
            labelFirst={true}
          ></Key>
        </div>
      </div>
      <div class='right-panel'>
        <div class='display-panel'>
          <div class='number'>{translateDialedNumber(dialedNumber)}</div>
          <div class='number'>{translateDialedNumber(dialedNumber)}</div>
          <div class='number'>{translateDialedNumber(dialedNumber)}</div>
          <div class='number'>{translateDialedNumber(dialedNumber)}</div>
        </div>

        <div class='keystrip-ca'>
          <Key
            valueVar={dialedAngle}
            label='dialed angle'
          ></Key>
          <Key
            valueVar={startDialAngle}
            label='start dial angle'
          ></Key>
          <Key
            valueVar={maxAngle}
            label='max angle'
          ></Key>
          <Key
            valueVar={angle}
            label='angle'
          ></Key>
        </div>
        <div class='keystrip-cb'>
          <Key
            lampClass={contextRunning ? "lamp-on lamp" : "lamp"}
            label='context'
            valueVar='CTX'
            onClick={contextKey}
          ></Key>
          <Key
            label='dial count'
            valueVar={dialCount}
          ></Key>
          <Key
            valueVar={dialedDigit}
            label='dialed digit'
          ></Key>
          <Key
            valueVar='SDR'
            label='sender'
            onClick={senderKey}
          ></Key>
        </div>
        <div class='keystrip-cc'>
          <Key
            onClick={releaseKey}
            valueVar='RLS'
            label='release'
          ></Key>
          <Key
            valueVar='TLK'
            label='talk'
            onClick={talkKey}
          ></Key>
          <Key
            valueVar='SDR'
            label='sender'
            onClick={senderKey}
          ></Key>
          <Key
            valueVar='END'
            label='end'
            onClick={endKey}
          ></Key>
        </div>
      </div>

      <div
        class='dial-panel'
        onMouseDown={fingerDown}
        onMouseUp={fingerUp}
        onMouseLeave={fingerUp}
      >
        <AnglePickerControl
          className='dial'
          withInputField={false}
          value={angle}
          onChange={angleDialed}
          __nextHasNoMarginBottom='true'
        />
        <div class='fingerstop'></div>
        <div class='keystrip-d'>
          <Key lampClass={dialNormal ? "lamp-on lamp" : "lamp"}></Key>
          <Key lampClass={dialNormal ? "lamp-on lamp" : "lamp"}></Key>
          <Key lampClass={dialing ? "lamp-on lamp" : "lamp"}></Key>
          <Key lampClass={pulsing ? "lamp-on lamp" : "lamp"}></Key>
        </div>
      </div>
    </div>
  );

  function hookFlash(e) {
    setDialedDigit("");
    setDialedNumber("");
  }

  function angleDialed(value) {
    setDialedAngle(value);
  }

  function fingerDown(e) {
    console.log("fingerDown  " + angle);
    // setDialing(true);
  }
  function fingerUp(e) {
    console.log("fingerUp  " + angle);
    setDialing(false);
  }

  function contextKey(e) {
    if (audioContext.current.state === "running") {
      audioContext.current.suspend();
    } else {
      audioContext.current.resume();
    }
    console.log("contextKey" + e);
  }

  function endKey(e) {
    console.log("endKey - audioElement: -- ");
    audioElement.current.pause();

    console.dir(audioElement.current);
  }

  function senderKey(e) {
    console.log("senderKey - audioElement: -- ");
    audioElement.current.play();

    console.dir(audioElement.current);
  }

  function talkKey(e) {
    console.log("talkKey" + e);

    audioElement.current = new Audio(
      "./../wp-content/plugins/elmercat/assets/audio/PraxLDO.mp3"
    );
    console.log("talkKey - audioElement: --");
    console.dir(audioElement.current);

    audioSource.current = audioContext.current.createMediaElementSource(
      audioElement.current
    );

    audioSource.current.connect(audioContext.current.destination);

    console.log("talkKey - audioSource: -- ");
    console.dir(audioSource.current);
  }

  function releaseKey(e) {
    console.log("releaseKey audioContext: -- ");
    console.dir(audioContext.current);
  }
  function keypressB(e) {
    console.log("keypressB" + e);
  }
  function keypressC(e) {
    console.log("keypressC" + e);
  }
  function keypressD(e) {
    console.log("keypressD" + e);
  }
}
